// Servicio de Pedidos de Compra (Purchase Orders)
// Maneja la creación y gestión de pedidos de compra a proveedores

import { createDocument, getDocument, getCollection, updateDocument, deleteDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'

const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders'
const SUPPLIES_COLLECTION = 'supplies'

/**
 * Generar número de pedido de compra único
 * Formato: PCO-YYYY-XXX (ej: PCO-2025-001)
 * @returns {Promise<string>} Número de pedido único
 */
const generatePurchaseOrderNumber = async () => {
  try {
    const year = new Date().getFullYear()
    const prefix = `PCO-${year}-`
    
    // Obtener todos los pedidos del año actual
    const orders = await getCollection(PURCHASE_ORDERS_COLLECTION, [])
    
    // Filtrar pedidos del año actual y extraer números
    const currentYearOrders = orders.filter(order => {
      if (!order.orderNumber) return false
      return order.orderNumber.startsWith(prefix)
    })
    
    // Encontrar el siguiente número
    let nextNumber = 1
    if (currentYearOrders.length > 0) {
      const numbers = currentYearOrders.map(order => {
        const numStr = order.orderNumber.replace(prefix, '')
        return parseInt(numStr, 10) || 0
      })
      const maxNumber = Math.max(...numbers)
      nextNumber = maxNumber + 1
    }
    
    // Formatear con ceros a la izquierda (001, 002, etc.)
    const formattedNumber = String(nextNumber).padStart(3, '0')
    return `${prefix}${formattedNumber}`
  } catch (error) {
    console.error('Error generando número de pedido de compra:', error)
    // Fallback: usar timestamp
    const timestamp = Date.now().toString().slice(-6)
    return `PCO-${new Date().getFullYear()}-${timestamp}`
  }
}

/**
 * Agregar entrada al historial
 * @param {Object} order - Pedido actual
 * @param {string} action - Acción realizada
 * @param {string} description - Descripción de la acción
 * @param {string} userId - UID del usuario
 * @param {string} userName - Nombre del usuario
 * @param {string} newStatus - Nuevo estado (opcional)
 * @returns {Object} Nueva entrada de historial
 */
const addHistoryEntry = (order, action, description, userId, userName, newStatus = null) => {
  // Asegurar que history sea un array
  let history = order.history || []
  
  // Si history es un objeto, convertirlo a array
  if (!Array.isArray(history)) {
    history = Object.values(history).sort((a, b) => {
      const aTime = a.timestamp?.toMillis?.() || 0
      const bTime = b.timestamp?.toMillis?.() || 0
      return aTime - bTime
    })
  }
  
  // Agregar nueva entrada al array
  history.push({
    action,
    status: newStatus || order.status,
    description,
    timestamp: Timestamp.now(),
    userId: userId || null,
    userName: userName || 'Sistema'
  })
  
  return history
}

/**
 * Crear un nuevo pedido de compra
 * @param {Object} orderData - Datos del pedido
 * @returns {Promise<string>} ID del pedido creado
 */
export const createPurchaseOrder = async (orderData) => {
  try {
    const orderNumber = await generatePurchaseOrderNumber()
    
    // Calcular totales
    let subtotal = 0
    let totalItems = 0
    
    orderData.suppliers.forEach(supplier => {
      supplier.items.forEach(item => {
        totalItems += item.quantity
        subtotal += item.totalPrice
      })
    })
    
    const taxes = orderData.taxes || 0
    const shipping = orderData.shipping || 0
    const total = subtotal + taxes + shipping
    
    // Preparar datos del pedido
    const order = {
      orderNumber,
      status: 'pending',
      suppliers: orderData.suppliers.map(supplier => ({
        ...supplier,
        paidAmount: 0,
        paymentHistory: [],
        receivedAt: null,
        rating: null,
        ratingNotes: null
      })),
      totalItems,
      subtotal,
      taxes,
      shipping,
      total,
      totalPaid: 0,
      totalReceived: 0,
      expectedDeliveryDate: orderData.expectedDeliveryDate || null,
      notes: orderData.notes || null,
      createdBy: orderData.createdBy || null,
      lastUpdatedBy: orderData.createdBy || null,
      history: {
        0: {
          action: 'created',
          status: 'pending',
          description: 'Pedido de compra creado',
          timestamp: Timestamp.now(),
          userId: orderData.createdBy || null,
          userName: orderData.createdByName || 'Sistema'
        }
      }
    }
    
    const orderId = await createDocument(PURCHASE_ORDERS_COLLECTION, order)
    return orderId
  } catch (error) {
    console.error('Error creando pedido de compra:', error)
    throw error
  }
}

/**
 * Obtener un pedido de compra por ID
 * @param {string} orderId - ID del pedido
 * @returns {Promise<Object|null>} Pedido o null si no existe
 */
export const getPurchaseOrder = async (orderId) => {
  try {
    return await getDocument(PURCHASE_ORDERS_COLLECTION, orderId)
  } catch (error) {
    console.error('Error obteniendo pedido de compra:', error)
    throw error
  }
}

/**
 * Obtener todos los pedidos de compra
 * @param {Object} options - Opciones de consulta
 * @returns {Promise<Array>} Array de pedidos
 */
export const getAllPurchaseOrders = async (options = {}) => {
  try {
    const {
      status = null,
      supplierId = null,
      orderByField = 'createdAt',
      orderDirection = 'desc',
      limitResults = null
    } = options

    const filters = []
    
    if (status) {
      filters.push({ field: 'status', operator: '==', value: status })
    }
    
    // Obtener todos y filtrar por proveedor en el cliente si es necesario
    let orders = await getCollection(
      PURCHASE_ORDERS_COLLECTION,
      filters,
      orderByField,
      orderDirection,
      limitResults
    )
    
    // Filtrar por proveedor en el cliente
    if (supplierId) {
      orders = orders.filter(order => {
        if (!order.suppliers || !Array.isArray(order.suppliers)) return false
        return order.suppliers.some(supplier => supplier.supplierId === supplierId)
      })
    }
    
    return orders
  } catch (error) {
    console.error('Error obteniendo pedidos de compra:', error)
    throw error
  }
}

/**
 * Actualizar estado de un pedido de compra
 * @param {string} orderId - ID del pedido
 * @param {string} status - Nuevo estado
 * @param {string} userId - UID del usuario
 * @param {string} userName - Nombre del usuario
 * @returns {Promise<void>}
 */
export const updatePurchaseOrderStatus = async (orderId, status, userId, userName) => {
  try {
    const order = await getPurchaseOrder(orderId)
    if (!order) throw new Error('Pedido no encontrado')
    
    const updates = {
      status,
      lastUpdatedBy: userId || null
    }
    
    // Agregar timestamp según el estado
    if (status === 'confirmed' && !order.confirmedAt) {
      updates.confirmedAt = Timestamp.now()
    }
    if (status === 'cancelled' && !order.cancelledAt) {
      updates.cancelledAt = Timestamp.now()
    }
    if (status === 'completed' && !order.completedAt) {
      updates.completedAt = Timestamp.now()
    }
    
    // Agregar al historial
    const history = addHistoryEntry(
      order,
      status === 'cancelled' ? 'cancelled' : 'status_changed',
      `Estado cambiado a: ${status}`,
      userId || null,
      userName || 'Sistema',
      status
    )
    updates.history = history
    
    await updateDocument(PURCHASE_ORDERS_COLLECTION, orderId, updates)
  } catch (error) {
    console.error('Error actualizando estado del pedido de compra:', error)
    throw error
  }
}

/**
 * Registrar un pago a un proveedor en un pedido
 * @param {string} orderId - ID del pedido
 * @param {string} supplierId - ID del proveedor
 * @param {Object} paymentData - Datos del pago
 * @returns {Promise<void>}
 */
export const registerPayment = async (orderId, supplierId, paymentData) => {
  try {
    const order = await getPurchaseOrder(orderId)
    if (!order) throw new Error('Pedido no encontrado')
    
    const supplierIndex = order.suppliers.findIndex(s => s.supplierId === supplierId)
    if (supplierIndex === -1) throw new Error('Proveedor no encontrado en el pedido')
    
    const supplier = order.suppliers[supplierIndex]
    const newPaidAmount = (supplier.paidAmount || 0) + paymentData.amount
    
    // Validar que no exceda el subtotal
    if (newPaidAmount > supplier.subtotal) {
      throw new Error('El monto pagado no puede exceder el subtotal del proveedor')
    }
    
    // Agregar pago al historial
    const paymentHistory = supplier.paymentHistory || []
    paymentHistory.push({
      amount: paymentData.amount,
      paymentDate: paymentData.paymentDate || Timestamp.now(),
      paymentMethod: paymentData.paymentMethod,
      notes: paymentData.notes || null,
      receiptImages: paymentData.receiptImages || null, // Array de {fileId, url, thumbnailUrl}
      createdBy: paymentData.createdBy || null,
      createdByName: paymentData.createdByName || null
    })
    
    // Actualizar proveedor
    const updatedSuppliers = [...order.suppliers]
    updatedSuppliers[supplierIndex] = {
      ...supplier,
      paidAmount: newPaidAmount,
      paymentHistory
    }
    
    // Calcular total pagado
    const totalPaid = updatedSuppliers.reduce((sum, s) => sum + (s.paidAmount || 0), 0)
    
    // Determinar nuevo estado
    let newStatus = order.status
    if (totalPaid > 0 && totalPaid < order.total) {
      newStatus = 'partially_paid'
    } else if (totalPaid >= order.total) {
      newStatus = 'paid'
    }
    
    // Agregar al historial
    const history = addHistoryEntry(
      order,
      'payment',
      `Pago registrado: $${paymentData.amount.toLocaleString()} a ${supplier.supplierName}`,
      paymentData.createdBy || null,
      paymentData.createdByName || 'Sistema',
      newStatus
    )
    
    await updateDocument(PURCHASE_ORDERS_COLLECTION, orderId, {
      suppliers: updatedSuppliers,
      totalPaid,
      status: newStatus,
      lastUpdatedBy: paymentData.createdBy || null,
      history
    })
  } catch (error) {
    console.error('Error registrando pago:', error)
    throw error
  }
}

/**
 * Registrar recepción de productos
 * @param {string} orderId - ID del pedido
 * @param {string} supplierId - ID del proveedor
 * @param {Array} receivedItems - Items recibidos [{itemId, receivedQuantity, defectiveQuantity}]
 * @param {boolean} updateStock - Si actualizar stock automáticamente
 * @param {string} userId - UID del usuario
 * @param {string} userName - Nombre del usuario
 * @returns {Promise<void>}
 */
export const registerReceipt = async (orderId, supplierId, receivedItems, updateStock, userId, userName) => {
  try {
    const order = await getPurchaseOrder(orderId)
    if (!order) throw new Error('Pedido no encontrado')
    
    const supplierIndex = order.suppliers.findIndex(s => s.supplierId === supplierId)
    if (supplierIndex === -1) throw new Error('Proveedor no encontrado en el pedido')
    
    const supplier = order.suppliers[supplierIndex]
    const updatedItems = supplier.items.map(item => {
      const receivedItem = receivedItems.find(ri => ri.itemId === item.itemId)
      if (receivedItem) {
        const newReceivedQuantity = (item.receivedQuantity || 0) + receivedItem.receivedQuantity
        const newDefectiveQuantity = (item.defectiveQuantity || 0) + (receivedItem.defectiveQuantity || 0)
        
        // Validar que no exceda la cantidad solicitada
        if (newReceivedQuantity > item.quantity) {
          throw new Error(`La cantidad recibida de ${item.name} excede la cantidad solicitada`)
        }
        
        return {
          ...item,
          receivedQuantity: newReceivedQuantity,
          defectiveQuantity: newDefectiveQuantity
        }
      }
      return item
    })
    
    // Actualizar proveedor
    const updatedSuppliers = [...order.suppliers]
    const allItemsReceived = updatedItems.every(item => 
      (item.receivedQuantity || 0) >= item.quantity
    )
    
    updatedSuppliers[supplierIndex] = {
      ...supplier,
      items: updatedItems,
      receivedAt: allItemsReceived ? Timestamp.now() : supplier.receivedAt
    }
    
    // Calcular total recibido
    const totalReceived = updatedSuppliers.reduce((sum, s) => {
      return sum + s.items.reduce((itemSum, item) => {
        const received = (item.receivedQuantity || 0) - (item.defectiveQuantity || 0)
        return itemSum + (received * item.unitPrice)
      }, 0)
    }, 0)
    
    // Determinar nuevo estado
    let newStatus = order.status
    const allSuppliersReceived = updatedSuppliers.every(s => 
      s.items.every(item => (item.receivedQuantity || 0) >= item.quantity)
    )
    const someSuppliersReceived = updatedSuppliers.some(s => 
      s.items.some(item => (item.receivedQuantity || 0) > 0)
    )
    
    if (allSuppliersReceived) {
      newStatus = 'received'
    } else if (someSuppliersReceived) {
      newStatus = 'partially_received'
    }
    
    // Agregar al historial
    const history = addHistoryEntry(
      order,
      'received',
      `Recepción registrada: ${receivedItems.length} items de ${supplier.supplierName}`,
      userId,
      userName,
      newStatus
    )
    
    await updateDocument(PURCHASE_ORDERS_COLLECTION, orderId, {
      suppliers: updatedSuppliers,
      totalReceived,
      status: newStatus,
      lastUpdatedBy: userId,
      history
    })
    
    // Actualizar stock si se solicitó
    if (updateStock) {
      for (const receivedItem of receivedItems) {
        const item = updatedItems.find(i => i.itemId === receivedItem.itemId)
        const quantityToAdd = receivedItem.receivedQuantity - (receivedItem.defectiveQuantity || 0)
        if (!item || quantityToAdd <= 0) {
          continue
        }

        if (item.type === 'product' && item.productId) {
          try {
            const { setStock, createStockMovement, getStock } = await import('./stock.service')
            const { getAllWarehouses } = await import('./warehouses.service')
            
            const cleanProductId = (item.productId || '').trim()
            
            if (!cleanProductId) {
                console.warn('ID de producto inválido en recepción:', item)
                continue
            }

            const product = await getDocument('products', cleanProductId)
            if (product) {
              const warehouses = await getAllWarehouses()
              const activeWarehouses = warehouses.filter(w => w.active !== false)
              const warehouseId = activeWarehouses.length > 0 ? activeWarehouses[0].id : null
              const cleanWarehouseId = (warehouseId || '').trim()
              
              if (cleanWarehouseId) {
                const stockOptions = {
                  size: item.size || null,
                  color: item.color || null
                }
                const currentStock = await getStock(cleanProductId, cleanWarehouseId, stockOptions)
                const newQuantity = (currentStock?.quantity || 0) + quantityToAdd
                
                await setStock(cleanProductId, cleanWarehouseId, newQuantity, currentStock?.minStock ?? null, stockOptions)
                
                await createStockMovement({
                  productId: cleanProductId,
                  warehouseId: cleanWarehouseId,
                  type: 'entrada',
                  quantity: quantityToAdd,
                  reason: 'Compra a proveedor',
                  reference: orderId,
                  notes: `Pedido de compra: ${order.orderNumber}`,
                  size: item.size || null,
                  color: item.color || null
                })
              } else {
                  console.warn('No se encontró un depósito activo para actualizar stock')
              }
            } else {
                console.warn(`Producto no encontrado para actualización de stock: ${cleanProductId}`)
            }
          } catch (stockError) {
            console.warn('Error actualizando stock:', stockError)
          }
        }

        if (item.type === 'supply' && item.supplyId) {
          try {
            const cleanSupplyId = (item.supplyId || '').trim()
            if (!cleanSupplyId) {
              console.warn('ID de insumo inválido en recepción:', item)
              continue
            }

            const supply = await getDocument(SUPPLIES_COLLECTION, cleanSupplyId)
            if (supply && supply.trackStock) {
              const currentStock = parseFloat(supply.currentStock) || 0
              const newStock = currentStock + quantityToAdd
              await updateDocument(SUPPLIES_COLLECTION, cleanSupplyId, {
                currentStock: newStock,
                updatedAt: Timestamp.now(),
                lastUpdatedBy: userId || null
              })
            }
          } catch (supplyError) {
            console.warn('Error actualizando stock de insumo:', supplyError)
          }
        }
      }
    }
  } catch (error) {
    console.error('Error registrando recepción:', error)
    throw error
  }
}

/**
 * Finalizar pedido y calificar proveedores
 * @param {string} orderId - ID del pedido
 * @param {Array} ratings - Calificaciones [{supplierId, rating, notes}]
 * @param {string} userId - UID del usuario
 * @param {string} userName - Nombre del usuario
 * @returns {Promise<void>}
 */
export const completePurchaseOrder = async (orderId, ratings, userId, userName) => {
  try {
    const order = await getPurchaseOrder(orderId)
    if (!order) throw new Error('Pedido no encontrado')
    
    if (order.status !== 'received' && order.status !== 'partially_received') {
      throw new Error('Solo se pueden finalizar pedidos recibidos')
    }
    
    // Actualizar calificaciones en proveedores
    const updatedSuppliers = order.suppliers.map(supplier => {
      const rating = ratings.find(r => r.supplierId === supplier.supplierId)
      if (rating) {
        return {
          ...supplier,
          rating: rating.rating,
          ratingNotes: rating.notes || null
        }
      }
      return supplier
    })
    
    // Actualizar calificaciones en la colección de proveedores
    try {
      const { getSupplier, updateSupplier } = await import('./suppliers.service')
      
      for (const rating of ratings) {
        const supplier = await getSupplier(rating.supplierId)
        if (supplier) {
          // Calcular nuevo promedio de calificación
          const currentRating = supplier.rating || 0
          const currentOrders = supplier.totalPurchaseOrders || 0
          const newAverage = currentOrders > 0
            ? ((currentRating * currentOrders) + rating.rating) / (currentOrders + 1)
            : rating.rating
          
          await updateSupplier(rating.supplierId, {
            rating: newAverage,
            totalPurchaseOrders: (supplier.totalPurchaseOrders || 0) + 1,
            totalAmountPurchased: (supplier.totalAmountPurchased || 0) + 
              (order.suppliers.find(s => s.supplierId === rating.supplierId)?.subtotal || 0),
            lastPurchaseDate: Timestamp.now()
          })
        }
      }
    } catch (supplierError) {
      console.warn('Error actualizando calificaciones de proveedores:', supplierError)
      // No interrumpir la finalización si falla la actualización
    }
    
    // Agregar al historial
    const history = addHistoryEntry(
      order,
      'completed',
      'Pedido finalizado y proveedores calificados',
      userId,
      userName,
      'completed'
    )
    
    await updateDocument(PURCHASE_ORDERS_COLLECTION, orderId, {
      suppliers: updatedSuppliers,
      status: 'completed',
      completedAt: Timestamp.now(),
      lastUpdatedBy: userId,
      history
    })
  } catch (error) {
    console.error('Error finalizando pedido de compra:', error)
    throw error
  }
}

/**
 * Actualizar un pedido de compra
 * @param {string} orderId - ID del pedido
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updatePurchaseOrder = async (orderId, updates) => {
  try {
    await updateDocument(PURCHASE_ORDERS_COLLECTION, orderId, updates)
  } catch (error) {
    console.error('Error actualizando pedido de compra:', error)
    throw error
  }
}

/**
 * Eliminar un pedido de compra (solo si está pendiente)
 * @param {string} orderId - ID del pedido
 * @returns {Promise<void>}
 */
export const deletePurchaseOrder = async (orderId) => {
  try {
    const order = await getPurchaseOrder(orderId)
    if (!order) throw new Error('Pedido no encontrado')
    
    // Solo permitir eliminar si está pendiente (nunca cambió su estado)
    if (order.status !== 'pending') {
      throw new Error('Solo se pueden eliminar pedidos con estado pendiente')
    }
    
    // Verificar que no tenga historial (excepto la creación inicial)
    const history = order.history || []
    const hasStatusChanges = Array.isArray(history) 
      ? history.some(entry => 
          entry.action === 'status_changed' || 
          entry.action === 'cancelled' || 
          entry.action === 'confirmed'
        )
      : Object.values(history).some(entry => 
          entry.action === 'status_changed' || 
          entry.action === 'cancelled' || 
          entry.action === 'confirmed'
        )
    
    if (hasStatusChanges) {
      throw new Error('No se puede eliminar un pedido que ya ha cambiado de estado')
    }
    
    await deleteDocument(PURCHASE_ORDERS_COLLECTION, orderId)
  } catch (error) {
    console.error('Error eliminando pedido de compra:', error)
    throw error
  }
}

export default {
  createPurchaseOrder,
  getPurchaseOrder,
  getAllPurchaseOrders,
  updatePurchaseOrderStatus,
  registerPayment,
  registerReceipt,
  completePurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder
}

